import React, { useState } from 'react';
import { X, MapPin, Calendar, Clock, Home, Building2, Plus, Check, Phone } from 'lucide-react';
import PreferedDateAndTime from './PreferedDateAndTime';

// Using the original PreferedDateAndTime component - keeping it unchanged

const DeliveryModal = ({ 
  isOpen, 
  onClose, 
  patientDetails = {},
  labCartDetails = {},
  preferDate = '',
  preferTime = '',
  onDateChange = () => {},
  onTimeChange = () => {},
  limitTimeForLab = false
}) => {
  const [activeTab, setActiveTab] = useState('address');
  const [addressOption, setAddressOption] = useState('clinic');
  const [formData, setFormData] = useState({
    mobile: '',
    address1: '',
    address2: '',
    landmark: '',
    pincode: '',
    city: '',
    state: ''
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
        {/* Header with your custom gradient */}
        <div className="bg-gradient-to-r from-[#004172] to-[#3cba84] px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Delivery Details</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('address')}
            className={`flex-1 px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'address' 
                ? 'text-[#004172] border-b-2 border-[#004172] bg-gradient-to-r from-[#004172]/5 to-[#3cba84]/5' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Delivery Address
          </button>
          <button
            onClick={() => setActiveTab('datetime')}
            className={`flex-1 px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'datetime' 
                ? 'text-[#004172] border-b-2 border-[#004172] bg-gradient-to-r from-[#004172]/5 to-[#3cba84]/5' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Date & Time
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[55vh] overflow-y-auto">
          {activeTab === 'address' && (
            <div className="space-y-5">
              {/* Compact Address Type Selection */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Choose Address Type</h3>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setAddressOption('clinic')}
                    className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                      addressOption === 'clinic'
                        ? 'border-[#004172] bg-gradient-to-br from-[#004172]/10 to-[#3cba84]/10 text-[#004172]'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Building2 className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">Clinic</div>
                    <div className="text-xs text-gray-500">Use clinic location</div>
                    {addressOption === 'clinic' && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#3cba84] rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setAddressOption('patient')}
                    className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                      addressOption === 'patient'
                        ? 'border-[#004172] bg-gradient-to-br from-[#004172]/10 to-[#3cba84]/10 text-[#004172]'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Home className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">Patient</div>
                    <div className="text-xs text-gray-500">Registered address</div>
                    {addressOption === 'patient' && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#3cba84] rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setAddressOption('new')}
                    className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                      addressOption === 'new'
                        ? 'border-[#004172] bg-gradient-to-br from-[#004172]/10 to-[#3cba84]/10 text-[#004172]'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Plus className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">New</div>
                    <div className="text-xs text-gray-500">Custom address</div>
                    {addressOption === 'new' && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#3cba84] rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Address Content - More Compact */}
              {addressOption === 'clinic' && (
                <div className="bg-gradient-to-r from-[#004172]/5 to-[#3cba84]/5 rounded-lg p-4 border border-[#004172]/20">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Clinic Address</h4>
                      <p className="text-sm text-gray-600">
                        123 Healthcare Center, Medical District<br />
                        New Delhi, 110001, India
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {addressOption === 'patient' && (
                <div className="bg-gradient-to-r from-[#004172]/5 to-[#3cba84]/5 rounded-lg p-4 border border-[#004172]/20">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-full flex items-center justify-center flex-shrink-0">
                      <Home className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Patient Address</h4>
                      <p className="text-sm text-gray-600">
                        {patientDetails.address || 'No address on file'}<br />
                        {patientDetails.pinCode && `PIN: ${patientDetails.pinCode}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {addressOption === 'new' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-full flex items-center justify-center">
                      <Plus className="w-3 h-3 text-white" />
                    </div>
                    <h4 className="font-medium text-gray-900">Enter New Address</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                        placeholder="Enter mobile number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        <Home className="w-3 h-3" />
                        House/Flat Number *
                      </label>
                      <input
                        type="text"
                        value={formData.address1}
                        onChange={(e) => handleInputChange('address1', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                        placeholder="House no, Building name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={formData.address2}
                      onChange={(e) => handleInputChange('address2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                      placeholder="Area, Street, Sector, Village"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Landmark</label>
                      <input
                        type="text"
                        value={formData.landmark}
                        onChange={(e) => handleInputChange('landmark', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                        placeholder="Near famous place"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Pincode *</label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                        placeholder="6 digit pincode"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004172] focus:border-transparent text-sm"
                        placeholder="Enter city"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'datetime' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-[#004172] to-[#3cba84] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select Collection Date & Time
                </h3>
                <p className="text-sm text-gray-600">
                  Choose your preferred date and time for sample collection
                </p>
              </div>

              <PreferedDateAndTime
                preferDate={preferDate}
                preferTime={preferTime}
                changeDate={onDateChange}
                handleChange={onTimeChange}
                limitTimeForLab={limitTimeForLab}
                labStartTime={labCartDetails?.labStartTime}
                labEndTime={labCartDetails?.labEndTime}
                labType={labCartDetails?.labTestItems?.[0]?.labTestType}
              />

              {labCartDetails?.labStartTime && labCartDetails?.labEndTime && (
                <div className="mt-3 p-3 bg-gradient-to-r from-[#004172]/10 to-[#3cba84]/10 rounded-lg border border-[#004172]/20">
                  <p className="text-sm text-[#004172] flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Lab hours: {labCartDetails.labStartTime} - {labCartDetails.labEndTime}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-5 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          
          <div className="flex gap-3">
            {activeTab === 'address' && (
              <button
                onClick={() => setActiveTab('datetime')}
                className="px-6 py-2 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg hover:shadow-lg font-medium transition-all duration-200 transform hover:scale-105"
              >
                Next: Select Date & Time
              </button>
            )}
            
            {activeTab === 'datetime' && (
              <>
                <button
                  onClick={() => setActiveTab('address')}
                  className="px-5 py-2 text-[#004172] border border-[#004172] rounded-lg hover:bg-[#004172]/5 font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gradient-to-r from-[#3cba84] to-[#004172] text-white rounded-lg hover:shadow-lg font-medium transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Confirm Details
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryModal;